import { z } from "zod";

import { CENTS, clientSeedSchema, dropTableItemSchema, seedRevealSchema } from "./primitives.js";

// ---- battles: closed sets ------------------------------------------------------------------
// A battle is N seats opening the same ordered list of boxes round by round on one server-paced
// timeline; the seat with the highest (standard) or lowest (crazy) total value takes every item.
// Ported from boxed; v1 ships free-for-all standard and crazy. Teams, terminal, shared and group
// modes are follow-ups, which is why `mode` is a tuple and `teamCount` exists on the wire.

export const BATTLE_MODES = ["standard", "crazy"] as const;
export type BattleMode = (typeof BATTLE_MODES)[number];

export const BATTLE_STATUSES = ["created", "started", "completed"] as const;
export type BattleStatus = (typeof BATTLE_STATUSES)[number];

/** `none` for a bot seat and for a human seat before its battle completes. */
export const BATTLE_CLAIM_STATUSES = ["none", "unclaimed", "claimed"] as const;
export type BattleClaimStatus = (typeof BATTLE_CLAIM_STATUSES)[number];

export const BATTLE_SEAT_KINDS = ["empty", "player", "bot"] as const;
export type BattleSeatKind = (typeof BATTLE_SEAT_KINDS)[number];

export const BATTLE_PLAYER_COUNTS = [2, 3, 4, 6] as const;
export type BattlePlayerCount = (typeof BATTLE_PLAYER_COUNTS)[number];

export const MAX_BATTLE_ROUNDS = 50;

/** The client seed every bot seat rolls with (boxed parity). */
export const BOT_CLIENT_SEED = "bot";

/** One member per refusal cause of `invalid_battle_settings`; carried in `details.reason`. */
export const BATTLE_SETTINGS_ISSUES = [
  "mode",
  "player_count",
  "team_count",
  "round_count",
  "seat_index",
] as const;
export type BattleSettingsIssue = (typeof BATTLE_SETTINGS_ISSUES)[number];

// ---- battles: timing -----------------------------------------------------------------------
// The timeline is server-authoritative. `started_at`, the countdown and the interval are stored
// on the battle at start; every client and the reveal runner derive the same instants from them:
//   visualRevealAt(N) = startedAt + countdown + N × interval
//   completeAt        = startedAt + countdown + roundCount × interval
// Round N's results are published `BATTLE_ROUND_DATA_PREFETCH_MS` before its visual boundary so
// the reel never waits on data; the client still gates rendering on the visual timeline.

/** Numeric 3-2-1 before round 0. */
export const BATTLE_START_COUNTDOWN_MS = 3000;
export const BATTLE_ROUND_DATA_PREFETCH_MS = 350;
/** A lobby this old is filled with bots and started (boxed parity: `isBattleStale`). */
export const BATTLE_STALE_LOBBY_MS = 15 * 60_000;
/** A started battle whose heartbeat is this old has lost its reveal runner. */
export const BATTLE_STALLED_REVEAL_MS = 60_000;

/**
 * The reel's choreography, from which the interval is derived so N lanes fit in one round.
 * Mirrors `@loot-os/react` (`spinner/MotionSpinner.tsx` and `spinner/timings.ts`), which
 * drift-tests these values: spin = MOTION_ANIMATION_DURATION, stagger = MOTION_STAGGER_DELAY,
 * hold = MOTION_REVEAL_LINGER_MS, spinLead = the pre-spin hold plus the settle snap, show = the
 * prize popup.
 */
export const BATTLE_REEL_TIMINGS = {
  spinMs: 2500,
  quickSpinMs: 1500,
  staggerMs: 250,
  quickStaggerMs: 150,
  spinLeadMs: 550,
  showMs: 1000,
  holdMs: 1500,
  quickHoldMs: 1000,
} as const;

/** The round cadence: spin lead + spin + per-lane stagger + prize show + hold. */
export function battleRevealIntervalMs(args: { playerCount: number; fastSpin: boolean }): number {
  const t = BATTLE_REEL_TIMINGS;
  const spin = args.fastSpin ? t.quickSpinMs : t.spinMs;
  const stagger = args.fastSpin ? t.quickStaggerMs : t.staggerMs;
  const hold = args.fastSpin ? t.quickHoldMs : t.holdMs;
  return t.spinLeadMs + spin + stagger * Math.max(0, args.playerCount - 1) + t.showMs + hold;
}

export interface BattleRevealTimeline {
  /** Epoch ms at which each round's results are published. */
  dataRevealAtMs: number[];
  /** Epoch ms at which each round may render. */
  visualRevealAtMs: number[];
  /** Epoch ms at which the battle completes: one interval past the last round's boundary. */
  completeAtMs: number;
}

/** Every instant of one battle's timeline, from the values stored at start. */
export function battleRevealTimeline(args: {
  startedAtMs: number;
  countdownMs: number;
  intervalMs: number;
  roundCount: number;
}): BattleRevealTimeline {
  const count = Math.max(0, args.roundCount);
  const prefetchMs = Math.min(BATTLE_ROUND_DATA_PREFETCH_MS, Math.max(0, args.countdownMs));
  const visualRevealAtMs = Array.from(
    { length: count },
    (_, round) => args.startedAtMs + args.countdownMs + round * args.intervalMs,
  );
  return {
    visualRevealAtMs,
    dataRevealAtMs: visualRevealAtMs.map((at) => at - prefetchMs),
    completeAtMs: args.startedAtMs + args.countdownMs + count * args.intervalMs,
  };
}

// ---- battles: wire ---------------------------------------------------------------------------

export const battleModeSchema = z.enum(BATTLE_MODES);
export const battleStatusSchema = z.enum(BATTLE_STATUSES);
export const battleClaimStatusSchema = z.enum(BATTLE_CLAIM_STATUSES);
export const battleSeatKindSchema = z.enum(BATTLE_SEAT_KINDS);
export const battlePlayerCountSchema = z.union([
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(6),
]);

const seatIndexSchema = z.int().min(0).max(5);

/** POST /v1/battles. The creator takes seat 0; a box may repeat, one round per entry. */
export const createBattleRequestSchema = z.object({
  boxIds: z.array(z.string().min(1)).min(1).max(MAX_BATTLE_ROUNDS),
  playerCount: battlePlayerCountSchema,
  mode: battleModeSchema,
  /** false = left out of the lobby list; joinable by id (a share link). */
  isPublic: z.boolean().default(true),
  fastSpin: z.boolean().default(false),
  /** Fill every other seat with a bot and start at once. */
  startWithBots: z.boolean().default(false),
  clientSeed: clientSeedSchema,
  /** Guard against creating at a price the client didn't display. */
  expectedPriceCents: z.int().nonnegative().describe(CENTS).optional(),
});
export type CreateBattleRequest = z.infer<typeof createBattleRequestSchema>;

/** POST /v1/battles/{battleId}/join. Seat 0 is the creator's. */
export const joinBattleRequestSchema = z.object({
  seatIndex: seatIndexSchema,
  clientSeed: clientSeedSchema,
});
export type JoinBattleRequest = z.infer<typeof joinBattleRequestSchema>;

/** POST /v1/battles/{battleId}/bots (creator only). */
export const addBattleBotRequestSchema = z.object({ seatIndex: seatIndexSchema });
export type AddBattleBotRequest = z.infer<typeof addBattleBotRequestSchema>;

/** GET /v1/battles?status=… */
export const battleListQuerySchema = z.object({
  status: battleStatusSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/** Who sits in a seat, as other shoppers may see them: never an external id or an email. */
export const battleEndUserSchema = z.object({
  id: z.string(),
  displayName: z.string(),
});
export type BattleEndUser = z.infer<typeof battleEndUserSchema>;

export const battleSeatSchema = z.object({
  seatIndex: z.int(),
  kind: battleSeatKindSchema,
  /** Set for a `player` seat only. */
  endUser: battleEndUserSchema.nullable(),
  /** Set once the seat is filled; a bot's is "bot". */
  clientSeed: z.string().nullable(),
  /** null until the battle is completed. */
  isWinner: z.boolean().nullable(),
  /** Σ value of this seat's own opens; null until the battle is completed. */
  totalValueCents: z.number().nullable().describe(CENTS),
});
export type BattleSeat = z.infer<typeof battleSeatSchema>;

/** One round: the box, the drop-table version it is rolled against, and the price every seat pays. */
export const battleRoundSchema = z.object({
  roundIndex: z.int(),
  boxId: z.string(),
  boxVersionId: z.string(),
  priceCents: z.number().describe(CENTS),
});
export type BattleRound = z.infer<typeof battleRoundSchema>;

/** A pinned drop table, listed once per distinct version however many rounds use it. */
export const battleBoxVersionSchema = z.object({
  id: z.string(),
  boxId: z.string(),
  version: z.number(),
  name: z.string(),
  imageUrl: z.string(),
  priceCents: z.number().describe(CENTS),
  /** The pinned version's volatility, as its box shows it; null for a version stored before it was. */
  volatility: z.number().nullable(),
  dropTable: z.array(dropTableItemSchema),
});
export type BattleBoxVersion = z.infer<typeof battleBoxVersionSchema>;

/** One provably-fair open inside a battle: nonce = roundIndex × playerCount + seatIndex. */
export const battleOpenSchema = z.object({
  roundIndex: z.int(),
  seatIndex: z.int(),
  nonce: z.int(),
  /** resultNumber in [0, 999999]. */
  result: z.int(),
  itemIndex: z.int(),
  item: dropTableItemSchema,
});
export type BattleOpen = z.infer<typeof battleOpenSchema>;

/** The viewer's own seat, present when the request carried a session. */
export const battleMeSchema = z.object({
  seatIndex: z.int(),
  /** What this seat paid on entry: the battle price, winner or not. The claim debits nothing. */
  costCents: z.number().describe(CENTS),
  claimStatus: battleClaimStatusSchema,
  /** Set once the seat was sold back: what the sale credited, and when. Null while kept or unclaimed. */
  soldCents: z.number().nullable().describe(CENTS),
  soldAt: z.string().nullable(),
});
export type BattleMe = z.infer<typeof battleMeSchema>;

export const battleSummarySchema = z.object({
  id: z.string(),
  mode: battleModeSchema,
  status: battleStatusSchema,
  playerCount: z.int(),
  /** Reserved for team battles; always null in v1. */
  teamCount: z.int().nullable(),
  isPublic: z.boolean(),
  fastSpin: z.boolean(),
  /** What every seat pays on entry (create or join): Σ round prices. */
  priceCents: z.number().describe(CENTS),
  roundCount: z.int(),
  /** The ordered rounds (box, pinned version, price); a lobby card shows their boxes. */
  rounds: z.array(battleRoundSchema),
  seats: z.array(battleSeatSchema),
  /** Rounds whose results are visible; -1 before round 0. Equals roundCount - 1 once completed. */
  currentRevealedRound: z.int(),
  /** null until the battle is completed. */
  winnerSeatIndex: z.int().nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  /** Stored at start; null before. */
  revealIntervalMs: z.int().nullable(),
  startCountdownMs: z.int().nullable(),
  /**
   * What a non-winning human seat is minted at claim, snapshotted at creation; null for battles
   * created before the prize existed. Disclosed from the moment the battle exists.
   */
  loserPrizeItem: dropTableItemSchema.nullable(),
});
export type BattleSummary = z.infer<typeof battleSummarySchema>;

export const battleSchema = battleSummarySchema.extend({
  /** Provably-fair domain separator for every open in this battle. */
  salt: z.string(),
  /** The daily server seed every open used; null before start. */
  seedDate: z.string().nullable(),
  boxVersions: z.array(battleBoxVersionSchema),
  /** results[roundIndex] holds one open per seat, for rounds up to currentRevealedRound. */
  results: z.array(z.array(battleOpenSchema)),
  /** null without a session, or when the viewer holds no seat. */
  me: battleMeSchema.nullable(),
});
export type Battle = z.infer<typeof battleSchema>;

/** `serverTime` beside the battle: the client's clock anchor for the timeline. */
export const battleResponseSchema = z.object({
  battle: battleSchema,
  serverTime: z.string(),
});
export type BattleResponse = z.infer<typeof battleResponseSchema>;

export const battleListResponseSchema = z.object({
  data: z.array(battleSummarySchema),
  nextCursor: z.string().nullable(),
  serverTime: z.string(),
});
export type BattleListResponse = z.infer<typeof battleListResponseSchema>;

export const battleClaimedItemSchema = z.object({
  battleOpenId: z.string(),
  inventoryItemId: z.string(),
  roundIndex: z.int(),
  seatIndex: z.int(),
  item: dropTableItemSchema,
});
export type BattleClaimedItem = z.infer<typeof battleClaimedItemSchema>;

/** The loser prize a claim minted: one inventory unit whose provenance is the seat itself. */
export const battleClaimedPrizeSchema = z.object({
  inventoryItemId: z.string(),
  item: dropTableItemSchema,
});
export type BattleClaimedPrize = z.infer<typeof battleClaimedPrizeSchema>;

/**
 * POST /v1/battles/{battleId}/claim: the items won (none for a loser) and, for a loser on a battle
 * that carries one, the prize minted. No money moves: the entry (`costCents`) was debited when the
 * seat was taken.
 */
export const battleClaimSchema = z.object({
  battleId: z.string(),
  seatIndex: z.int(),
  /** What the seat paid on entry, for display; the claim itself debits nothing. */
  costCents: z.number().describe(CENTS),
  claimedAt: z.string(),
  items: z.array(battleClaimedItemSchema),
  prize: battleClaimedPrizeSchema.nullable(),
});
export type BattleClaim = z.infer<typeof battleClaimSchema>;

export const battleClaimResponseSchema = z.object({
  claim: battleClaimSchema,
  replayed: z.boolean(),
});
export type BattleClaimResponse = z.infer<typeof battleClaimResponseSchema>;

/**
 * POST /v1/battles/{battleId}/sell-back: the claim above, then every minted unit (items and prize)
 * sold back to the house for its value in the same transaction.
 */
export const battleSellBackSchema = battleClaimSchema.extend({
  /** Σ item.value credited by the sale. */
  soldCents: z.number().describe(CENTS),
  soldAt: z.string(),
  /** The balance once the sale is credited. */
  balanceAfterCents: z.number().describe(CENTS),
});
export type BattleSellBack = z.infer<typeof battleSellBackSchema>;

export const battleSellBackResponseSchema = z.object({
  sale: battleSellBackSchema,
  replayed: z.boolean(),
});
export type BattleSellBackResponse = z.infer<typeof battleSellBackResponseSchema>;

/** GET /v1/battles/{battleId}/verification: everything a completed battle's opens need. */
export const battleVerificationSchema = z.object({
  battleId: z.string(),
  mode: battleModeSchema,
  playerCount: z.int(),
  salt: z.string(),
  seed: seedRevealSchema,
  seats: z.array(
    z.object({
      seatIndex: z.int(),
      kind: battleSeatKindSchema,
      clientSeed: z.string().nullable(),
      isWinner: z.boolean(),
      totalValueCents: z.number().describe(CENTS),
    }),
  ),
  rounds: z.array(battleRoundSchema),
  boxVersions: z.array(battleBoxVersionSchema),
  opens: z.array(battleOpenSchema),
  winnerSeatIndex: z.int(),
});
export type BattleVerification = z.infer<typeof battleVerificationSchema>;

// ---- realtime -------------------------------------------------------------------------------
// Live updates ride AppSync Events: the browser opens a WebSocket to `realtimeEndpoint` with a
// short-lived token the platform mints, then subscribes to channels under `/battles/{environmentId}`.

/** Both "" = this stage has no Event API; the storefront polls instead. */
export const storefrontRealtimeSchema = z.object({
  /** The Event API's HTTP hostname: the `host` in the WebSocket auth header. */
  httpEndpoint: z.string(),
  /** The Event API's realtime hostname: where the WebSocket connects. */
  realtimeEndpoint: z.string(),
});
export type StorefrontRealtime = z.infer<typeof storefrontRealtimeSchema>;

/** POST /v1/realtime/token */
export const realtimeTokenResponseSchema = storefrontRealtimeSchema.extend({
  token: z.string(),
  expiresAt: z.string(),
});
export type RealtimeTokenResponse = z.infer<typeof realtimeTokenResponseSchema>;

const eventBase = { battleId: z.string(), timestamp: z.string() };

/**
 * What the battle channel carries. `BATTLE_CREATED` reaches the lobby channel only; the summary
 * events reach both for a public battle and the battle channel alone for a private one. Clients
 * treat a summary as a hint and refetch the full battle; `ROUND_REVEALED` is merged directly.
 */
export const battleEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("BATTLE_CREATED"), ...eventBase, battle: battleSummarySchema }),
  z.object({
    type: z.literal("PLAYER_JOINED"),
    ...eventBase,
    seatIndex: z.int(),
    battle: battleSummarySchema,
  }),
  z.object({ type: z.literal("BATTLE_STARTED"), ...eventBase, battle: battleSummarySchema }),
  z.object({
    type: z.literal("ROUND_REVEALED"),
    ...eventBase,
    round: z.int(),
    results: z.array(battleOpenSchema),
  }),
  z.object({
    type: z.literal("BATTLE_COMPLETED"),
    ...eventBase,
    winnerSeatIndices: z.array(z.int()),
    battle: battleSummarySchema,
  }),
]);
export type BattleEvent = z.infer<typeof battleEventSchema>;
export type BattleEventType = BattleEvent["type"];

/** The one channel namespace; paths are `/battles/{environmentId}/lobby` and `/battles/{environmentId}/{battleId}`. */
export const BATTLE_CHANNEL_NAMESPACE = "battles";
export function battleLobbyChannel(environmentId: string): string {
  return `/${BATTLE_CHANNEL_NAMESPACE}/${environmentId}/lobby`;
}
export function battleChannel(environmentId: string, battleId: string): string {
  return `/${BATTLE_CHANNEL_NAMESPACE}/${environmentId}/${battleId}`;
}
